import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Question, QuestionType } from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';

/** One identity record (from the document, or from the user's stored PII). */
export interface KycPreReviewIdentity {
  fullName: string | null;
  cpf: string | null;
  cpfDigits: string | null;
  dobIso: string | null;
  /**
   * Extra OCR name candidates (no-QR Textract path) the scorer can also test against the user's
   * name — the best candidate wins. The modal itself only renders `fullName`; this is optional and
   * unused on the QR path.
   */
  nameAlternates?: string[];
}

/**
 * Configurable "rough match" tolerance for the comparison. Optional and additive: when omitted
 * (e.g. the QR path) the matchers keep their original strict behaviour. Each knob can only LOOSEN
 * the match, never tighten it, so passing tolerance never changes a previously-matching field to a
 * mismatch. Used by the no-QR (Textract OCR) path where read errors are more likely.
 */
export interface KycPreReviewTolerance {
  /** 0..1 — also accept a name when the token-overlap ratio is at least this. 0/undefined = off. */
  nameTokenOverlapThreshold?: number;
  /** Allow up to this many differing CPF digits (same length) to still count as a match. 0 = exact. */
  cpfMaxDigitDistance?: number;
}

/** Data passed into the pre-review modal. */
export interface KycPreReviewData {
  /** Identity extracted from the uploaded document (via brdocs QR or the Textract OCR pre-check). */
  extracted: KycPreReviewIdentity;
  /** Identity currently on file for the user. */
  user: KycPreReviewIdentity;
  /** Human title of the recognised document, e.g. "CNH-e". */
  documentTitle: string;
  /** Optional rough-match tolerance (no-QR path). Omitted on the QR path → strict matching. */
  tolerance?: KycPreReviewTolerance;
  /**
   * Offer the "amend my details" action (no-QR / OCR path only). When false/omitted (QR path) the
   * modal behaves exactly as the original: no amend button, and Confirm adopts the document's values.
   */
  allowAmend?: boolean;
}

/** What the user decided in the pre-review. */
export type KycPreReviewResult =
  // It's me and my saved details look right — proceed; the caller fills any blank PII (non-destructive).
  | { action: 'confirm' }
  // No-QR path: the user picked the correct value for each differing field ("select the correct
  // data" stepper). `resolved` carries only the reconciled fields; the caller OVERWRITES the PII
  // with them. Fields that already matched are left null (not written).
  | { action: 'reconcile'; resolved: KycPreReviewIdentity }
  | { action: 'proceed'; explanation: string }
  | { action: 'restart'; explanation?: string };

type FieldStatus = 'match' | 'mismatch' | 'missing';
type FieldKey = 'name' | 'cpf' | 'dob';
/** Which value the user chose for a field in the stepper. */
type DataChoice = 'doc' | 'account' | 'manual';

interface ComparedField {
  key: FieldKey;
  label: string;
  docValue: string;
  userValue: string;
  status: FieldStatus;
}

/**
 * SomosKycV1 pre-review (step 1, frontend pre-check).
 *
 * Shows the identity extracted from the uploaded document next to the user's
 * stored PII and asks them to confirm it is really their document and data.
 *
 *  - Confirm  → proceed to the KYC submission.
 *  - Don't confirm → an explanation box opens; the user either proceeds anyway
 *    (typing why they're using a document that isn't theirs) or restarts with a
 *    new document. Either non-confirmation is logged by the caller.
 */
@Component({
  selector: 'app-kyc-pre-review-modal',
  templateUrl: './kyc-pre-review-modal.component.html',
  styleUrls: ['./kyc-pre-review-modal.component.scss'],
})
export class KycPreReviewModalComponent {
  fields: ComparedField[] = [];
  /** True when every present field matches and nothing conflicts. */
  allMatch = false;

  /** Toggles the "this isn't quite right" explanation panel. */
  showExplanation = false;
  explanation = '';

  /**
   * No-QR path only: render the "select the correct data" stepper (one step per differing field,
   * each offering document / account / type-again) instead of the strict QR confirm view.
   */
  allowAmend = false;

  // ── "Select the correct data" stepper state (no-QR path) ──────────────────────────────────────
  /** The fields that need a decision — anything that didn't cleanly match. One step each. */
  steps: ComparedField[] = [];
  /** Index of the field currently being decided. */
  stepIndex = 0;
  /** The user's pick per field. */
  choice: Record<FieldKey, DataChoice | null> = { name: null, cpf: null, dob: null };
  /** The hand-typed value per field (used when choice === 'manual'). DOB is ISO yyyy-mm-dd. */
  manual: Record<FieldKey, string> = { name: '', cpf: '', dob: '' };

  /** Plain CPF question backing the "type again" masked input (no leak check). */
  manualCpfQuestion: Question = {
    id: 'pre-review-manual-cpf',
    created_at: new Date(),
    name: 'CPF',
    slug: 'pre-review-manual-cpf',
    statement: '',
    response_type: QuestionType.CPF,
    configuration: null,
    questionnaire_id: '',
    elective: false,
    answer: undefined,
    isValid: false,
  } as Question;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KycPreReviewData,
    private dialogRef: MatDialogRef<KycPreReviewModalComponent, KycPreReviewResult>,
  ) {
    this.fields = this.compare(data.extracted, data.user);
    // A document that carried no name (e.g. a CIN QR encodes only CPF + DOB) gives nothing to
    // confirm or reconcile about the name — drop the name row entirely rather than show a blank "—".
    if (!(data.extracted.fullName ?? '').trim()) {
      this.fields = this.fields.filter((f) => f.key !== 'name');
    }
    this.allMatch =
      this.fields.length > 0 && this.fields.every((f) => f.status === 'match');
    this.allowAmend = !!data.allowAmend;
    // Stepper covers every field that isn't a clean match (a real conflict, or a gap on one side).
    this.steps = this.fields.filter((f) => f.status !== 'match');
  }

  // ==================== QR-PATH ACTIONS ====================

  confirm(): void {
    this.dialogRef.close({ action: 'confirm' });
  }

  /** User says the data is wrong / not theirs — reveal the explanation panel. */
  reject(): void {
    this.showExplanation = true;
  }

  // ==================== "SELECT THE CORRECT DATA" STEPPER (no-QR) ====================

  get currentStep(): ComparedField | undefined {
    return this.steps[this.stepIndex];
  }
  get totalSteps(): number {
    return this.steps.length;
  }
  get stepNumber(): number {
    return this.stepIndex + 1;
  }
  get isFirstStep(): boolean {
    return this.stepIndex === 0;
  }
  get isLastStep(): boolean {
    return this.stepIndex >= this.steps.length - 1;
  }

  /** Whether the document / account option has a real value to offer for a field. */
  optionAvailable(key: FieldKey, source: 'doc' | 'account'): boolean {
    return !!this.rawValue(key, source);
  }

  /** Record the user's pick for the current field; pre-fill the manual box on first "type again". */
  selectChoice(key: FieldKey, choice: DataChoice): void {
    this.choice[key] = choice;
    if (choice === 'manual' && !this.manual[key]) {
      // Seed the box with whatever's on file so the user tweaks rather than retypes from scratch.
      this.manual[key] =
        key === 'dob' ? this.dobInputValue(this.data.user.dobIso) : this.rawValue(key, 'account') ?? '';
    }
  }

  /** Masked CPF widget → store the hand-typed CPF (canAdvance() checks it's non-empty). */
  onManualCpfChange(a: QuestionAnswer): void {
    this.manual.cpf = (a?.answer?.value ?? '').toString();
  }

  /** Date widget → store the hand-typed DOB as ISO yyyy-mm-dd. */
  onManualDobChange(d: Date | null): void {
    if (d instanceof Date && !isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      this.manual.dob = `${y}-${m}-${day}`;
    } else {
      this.manual.dob = '';
    }
  }

  /** Date object for the DOB widget, seeded from the ISO manual value. */
  get manualDobDate(): Date | null {
    const iso = this.manual.dob;
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  /** Can we leave the current step? A choice must be made (and a manual value typed). */
  canAdvance(): boolean {
    const f = this.currentStep;
    if (!f) return true; // no steps → nothing to decide
    const c = this.choice[f.key];
    if (!c) return false;
    if (c === 'manual') return this.manual[f.key].trim().length > 0;
    return true;
  }

  back(): void {
    if (this.stepIndex > 0) this.stepIndex--;
  }

  /** Advance to the next field, or finish on the last one. */
  next(): void {
    if (!this.canAdvance()) return;
    if (this.isLastStep) {
      this.finishReconcile();
      return;
    }
    this.stepIndex++;
  }

  /** Build the reconciled identity from the per-field choices and close. */
  private finishReconcile(): void {
    if (!this.canAdvance()) return;
    const resolved: KycPreReviewIdentity = {
      fullName: null,
      cpf: null,
      cpfDigits: null,
      dobIso: null,
    };
    for (const f of this.steps) {
      const c = this.choice[f.key];
      if (!c) continue;
      if (f.key === 'name') {
        resolved.fullName = this.resolvedValue('name', c);
      } else if (f.key === 'cpf') {
        resolved.cpf = this.resolvedValue('cpf', c);
        resolved.cpfDigits = this.resolvedCpfDigits(c);
      } else if (f.key === 'dob') {
        resolved.dobIso = this.resolvedValue('dob', c);
      }
    }
    this.dialogRef.close({ action: 'reconcile', resolved });
  }

  /** Raw (un-formatted) value of a field from the document or the account. */
  private rawValue(key: FieldKey, source: 'doc' | 'account'): string | null {
    const id = source === 'doc' ? this.data.extracted : this.data.user;
    const v = key === 'name' ? id.fullName : key === 'cpf' ? id.cpf : id.dobIso;
    return (v ?? '').trim() || null;
  }

  /** The value to persist for a field given the chosen source. */
  private resolvedValue(key: FieldKey, c: DataChoice): string | null {
    if (c === 'manual') {
      if (key === 'dob') return this.manual.dob.trim() || null;
      return this.manual[key].trim() || null;
    }
    return this.rawValue(key, c === 'doc' ? 'doc' : 'account');
  }

  /** CPF digits for the chosen source (id_document_value is stored as digits). */
  private resolvedCpfDigits(c: DataChoice): string | null {
    if (c === 'manual') return this.manual.cpf.replace(/\D/g, '') || null;
    const id = c === 'doc' ? this.data.extracted : this.data.user;
    return (id.cpfDigits || (id.cpf ?? '').replace(/\D/g, '')) || null;
  }

  /** ISO yyyy-mm-dd as the native date input expects it; blank for anything non-ISO/null. */
  private dobInputValue(iso: string | null): string {
    return iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '';
  }

  /** Proceed despite not confirming — requires an explanation. */
  proceedAnyway(): void {
    const explanation = this.explanation.trim();
    if (!explanation) return;
    this.dialogRef.close({ action: 'proceed', explanation });
  }

  /** Abandon this document and restart the KYC upload with a new one. */
  restart(): void {
    const explanation = this.explanation.trim();
    this.dialogRef.close({ action: 'restart', explanation: explanation || undefined });
  }

  // ==================== COMPARISON ====================

  private compare(doc: KycPreReviewIdentity, user: KycPreReviewIdentity): ComparedField[] {
    return [
      this.field('name', 'KycPreReviewFieldName', doc.fullName, user.fullName, (a, b) =>
        this.namesMatch(a, b),
      ),
      this.field('cpf', 'KycPreReviewFieldCpf', doc.cpf, user.cpf, () =>
        this.digitsMatch(doc.cpfDigits, user.cpfDigits),
      ),
      this.field(
        'dob',
        'KycPreReviewFieldDob',
        this.dobLabel(doc.dobIso),
        this.dobLabel(user.dobIso),
        () => this.equal(doc.dobIso, user.dobIso),
      ),
    ];
  }

  private field(
    key: ComparedField['key'],
    label: string,
    docValue: string | null,
    userValue: string | null,
    matcher: (a: string, b: string) => boolean,
  ): ComparedField {
    const dv = (docValue ?? '').trim();
    const uv = (userValue ?? '').trim();
    let status: FieldStatus;
    if (!dv || !uv) {
      status = 'missing';
    } else {
      status = matcher(dv, uv) ? 'match' : 'mismatch';
    }
    return { key, label, docValue: dv || '—', userValue: uv || '—', status };
  }

  /** Accent/case/space-insensitive name match: tokens of one are a subset of the other. */
  private namesMatch(a: string, b: string): boolean {
    const ta = this.tokens(a);
    const tb = this.tokens(b);
    if (!ta.length || !tb.length) return false;
    if (ta.join(' ') === tb.join(' ')) return true;
    const [short, long] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
    if (short.every((t) => long.includes(t))) return true;
    // Optional (no-QR) tolerance: accept when enough tokens overlap, for OCR'd names with a
    // misread/missing token. Off by default (threshold 0/undefined) → QR path unchanged.
    const threshold = this.data.tolerance?.nameTokenOverlapThreshold ?? 0;
    if (threshold > 0) {
      const overlap = short.filter((t) => long.includes(t)).length / short.length;
      if (overlap >= threshold) return true;
    }
    return false;
  }

  private tokens(s: string): string[] {
    return this.fold(s).split(/\s+/).filter(Boolean);
  }

  private fold(s: string): string {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  private digitsMatch(a: string | null, b: string | null): boolean {
    if (!a || !b) return false;
    const da = a.replace(/\D/g, '');
    const db = b.replace(/\D/g, '');
    if (da === db) return true;
    // Optional (no-QR) tolerance: allow a few misread digits when same length. 0 = exact (default).
    const maxDistance = this.data.tolerance?.cpfMaxDigitDistance ?? 0;
    if (maxDistance > 0 && da.length === db.length) {
      let diff = 0;
      for (let i = 0; i < da.length; i++) if (da[i] !== db[i]) diff++;
      return diff <= maxDistance;
    }
    return false;
  }

  private equal(a: string | null, b: string | null): boolean {
    return !!a && !!b && a === b;
  }

  /** Render an ISO date as dd/mm/yyyy for display; pass through anything else. */
  private dobLabel(iso: string | null): string | null {
    if (!iso) return null;
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  }
}
